import React, {useEffect,useRef,useState} from "react";
import {Alert,Button,Table} from "react-bootstrap";
import axios from "axios";
import {API_URL} from "../../../../config";
import {isCalendarDate, materialTurnsText, monthKey, monthYear, overrideReasonWords, shortDate, turnsChangeText} from "./rsoForecastWindowHelpers";
import RsoSignInExpired, {isSignInExpired} from "./RsoSignInExpired";

// Forecasts Sales sent outside the monthly input week, with the sign-off recorded before each one was sent.
// GET Dashboard/api/RsoPropertyForecast/overrides: the rep's assigned properties, newest first, a cursor per page.
const endpoint = `${API_URL}/Dashboard/api/RsoPropertyForecast/overrides`;
const missing = "Not recorded";
const isRecord = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const text = (value) => typeof value === "string" && value.trim() ? value.trim() : missing;
const sentAt = (value) => new Date(value).toLocaleString();
const validInstant = (value) => typeof value === "string" && !Number.isNaN(new Date(value).getTime());
const turns = (value) => value == null || (Number.isSafeInteger(value) && value >= 0);
const validLine = (line) => isRecord(line) && turns(line.beforeUnitsTurning) && turns(line.afterUnitsTurning);
// Every row must be complete: one malformed item hides the whole list rather than showing half a record.
const validItem = (item) => isRecord(item) && typeof item.id === "string" && !!item.id
  && Number.isSafeInteger(Number(item.contactInfoId)) && Number(item.contactInfoId) > 0 && !!monthKey(item.forecastMonth)
  && ["first_answer","change"].includes(item.case) && !!overrideReasonWords(item.reason) && typeof item.noUnits === "boolean"
  && Array.isArray(item.lines) && item.lines.every(validLine) && isRecord(item.submittedBy) && validInstant(item.sentAt)
  && isRecord(item.signOff) && isCalendarDate(item.signOff.toldPurchasingOn) && typeof item.signOff.note === "string";
// What the override sent: a first answer's materials and turns, the turns before → after, or No units.
const changes = (item) => {
  const lines = item.lines.map((line) => ({name:line.materialName,before:line.beforeUnitsTurning,after:line.afterUnitsTurning}));
  if (item.noUnits) {
    const replaced = item.case === "change" ? lines.filter((line) => line.before > 0) : [];
    return ["No units this month",...(replaced.length ? [`Replaces ${replaced.map(({name,before}) => materialTurnsText(name,before)).join(", ")}`] : [])];
  }
  return lines.map(({name,before,after}) => item.case === "change" ? turnsChangeText(name,before,after) : materialTurnsText(name,after));
};

function OverrideRow({item}) {
  return <tr>
    <td>{sentAt(item.sentAt)}</td>
    <td>{text(item.propertyName)}</td>
    <td>{monthYear(item.forecastMonth)}<small className="d-block">{overrideReasonWords(item.reason)}</small></td>
    <td><span className="rsf-override-case">{item.case === "first_answer" ? "First answer" : "Change"}</span>
      <ul className="rsf-override-changes">{changes(item).map((line,index) => <li key={index}>{line}</li>)}</ul></td>
    <td>{shortDate(item.signOff.toldPurchasingOn)}</td>
    <td className="rsf-override-note">{text(item.signOff.note)}</td>
    <td>{text(item.submittedBy.displayName)}</td>
  </tr>;
}

export default function RsoOverrideHistory({token,active}) {
  const [page,setPage] = useState({items:[],nextCursor:null,status:"idle",key:""});
  const [request,setRequest] = useState({cursor:null,attempt:0,key:""});
  const currentKey = useRef("");
  const key = active && token ? token : "";
  currentKey.current = key;
  const cursor = request.key === key ? request.cursor : null;
  useEffect(() => {
    if (!key) {setPage({items:[],nextCursor:null,status:"idle",key:""});setRequest({cursor:null,attempt:0,key:""});return undefined;}
    const controller=new AbortController();let current=true;
    setPage((old) => ({...(cursor && old.key === key ? old : {items:[],nextCursor:null}),status:"loading",key}));
    axios.get(endpoint,{headers:{Authorization:`Bearer ${token}`},params:cursor ? {cursor} : {},signal:controller.signal,timeout:30000}).then(({data}) => {
      if (!current || currentKey.current !== key) return;
      if (!Array.isArray(data?.items) || !data.items.every(validItem)) {
        const error=new Error("Invalid overrides");
        error.invalidResponse=true;
        throw error;
      }
      setPage((old) => {
        const prior=cursor && old.key === key ? old.items : [];
        const ids=new Set(prior.map((item) => item.id));
        return {items:[...prior,...data.items.filter((item) => !ids.has(item.id))],nextCursor:data.nextCursor === cursor ? null : data.nextCursor || null,status:"ready",key};
      });
    }).catch((error) => {
      if (!current || currentKey.current !== key) return;
      // A bad page or lost access shows nothing rather than an incomplete list; Try again starts from the newest.
      // An expired sign-in shows the sign-in message instead of Try again.
      const signInExpired=isSignInExpired(error);
      const clear=error?.invalidResponse || signInExpired || error?.response?.status === 403;
      setPage((old) => ({...(old.key === key && !clear ? old : {items:[],nextCursor:null}),status:"error",key,resetCursor:clear,signInExpired}));
    });
    return () => {current=false;controller.abort();};
  },[key,token,cursor,request.attempt]);
  const visible=page.key === key ? page : {items:[],nextCursor:null,status:"loading"};
  return <section className="rsf-request-history rsf-overrides" aria-label="Overrides">
    <h4 tabIndex="-1">Overrides</h4>
    <p>Forecasts sent outside the monthly input week. Purchasing did not see these in the normal window.</p>
    <Table responsive className="rsf-overrides-table"><thead><tr>
      <th scope="col">Sent</th>
      <th scope="col">Property</th>
      <th scope="col">Month</th>
      <th scope="col">What changed</th>
      <th scope="col">Told Purchasing on</th>
      <th scope="col">Note</th>
      <th scope="col">Signed by</th>
    </tr></thead>
      <tbody>{visible.items.map((item) => <OverrideRow key={item.id} item={item} />)}</tbody></Table>
    {visible.status === "loading" && <p role="status">Loading overrides…</p>}
    {visible.status === "error" && (visible.signInExpired ? <RsoSignInExpired />
      : <Alert variant="danger">Could not load overrides. <Button variant="link" onClick={() => setRequest({key,cursor:visible.resetCursor ? null : cursor,attempt:request.attempt+1})}>Try again</Button></Alert>)}
    {visible.status === "ready" && !visible.items.length && !visible.nextCursor && <p>No overrides yet.</p>}
    {visible.status === "ready" && !!visible.items.length && <p role="status" aria-live="polite">
      {`Showing ${visible.items.length} override${visible.items.length === 1 ? "" : "s"}${visible.nextCursor ? " loaded" : ""}.`}</p>}
    {visible.nextCursor && visible.status === "ready" && <Button variant="outline-primary" onClick={() => setRequest({key,cursor:visible.nextCursor,attempt:request.attempt+1})}>Load more</Button>}
  </section>;
}
